import { useCallback, useEffect, useState } from 'react';
import type { SetlistItem, SetlistSection } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import type { LiveStateAction } from '../api/showClient.js';
import { useShowClient } from '../hooks/useShowClient.js';
import { flattenItems } from '../lib/setlist.js';

/**
 * Set-list view for the Performance Console (§24.1, §17.4). A large, glanceable, dark list of the
 * current and upcoming items with one-tap current-item controls (start / complete / skip / return /
 * clear), a break start/end toggle, and offline-aware buttons: while offline, state-changing
 * actions are disabled and marked pending rather than silently queued (§24.5). "Skip" is styled
 * distinctly from "Complete" so they are never confused (§24.3).
 */

export interface SetlistConsoleViewProps {
  gigId: string;
  /** Current item id from the gig snapshot, used as the initial selection. */
  initialCurrentItemId: string | null;
  /** Browser connectivity — drives the offline "pending / disabled" treatment (§24.5). */
  online: boolean;
}

interface Row {
  item: SetlistItem;
  sectionName: string;
}

function toRows(sections: SetlistSection[]): Row[] {
  return sections.flatMap((s) => s.items.map((item) => ({ item, sectionName: s.name })));
}

export function SetlistConsoleView({ gigId, initialCurrentItemId, online }: SetlistConsoleViewProps) {
  const client = useShowClient();
  const [sections, setSections] = useState<SetlistSection[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentItemId, setCurrentItemId] = useState<string | null>(initialCurrentItemId);
  const [breakActive, setBreakActive] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const setlist = await client.getSetlist(gigId);
      setSections(setlist.sections);
      setLoaded(true);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load the set list.');
    }
  }, [client, gigId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setCurrentItemId(initialCurrentItemId);
  }, [initialCurrentItemId]);

  const runAction = useCallback(
    async (label: string, action: LiveStateAction) => {
      if (!online) return; // Never queue state-changing actions offline (§24.5).
      setPending(label);
      setActionError(null);
      try {
        const result = await client.patchLiveState(gigId, { action });
        setCurrentItemId(result.currentSetlistItemId);
      } catch (err) {
        setActionError(err instanceof ApiError ? err.message : 'Action failed.');
      } finally {
        setPending(null);
      }
    },
    [client, gigId, online],
  );

  const toggleBreak = useCallback(async () => {
    if (!online) return;
    const next = !breakActive;
    setPending('break');
    setActionError(null);
    try {
      await client.patchLiveState(gigId, { breakActive: next });
      setBreakActive(next);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not toggle the break.');
    } finally {
      setPending(null);
    }
  }, [client, gigId, online, breakActive]);

  const rows = toRows(sections);
  const items = flattenItems(sections);
  const currentIndex = currentItemId ? items.findIndex((it) => it.itemId === currentItemId) : -1;
  const current = currentIndex >= 0 ? items[currentIndex] : null;
  const disabled = !online || pending !== null;

  return (
    <section className="console__setlist" aria-label="Set list">
      <div className="console__setlist-head">
        <h2>Set list</h2>
        <button
          type="button"
          className={`big ${breakActive ? 'danger' : ''}`}
          disabled={disabled}
          aria-pressed={breakActive}
          onClick={() => void toggleBreak()}
        >
          {breakActive ? 'End break' : 'Start break'}
        </button>
      </div>

      {!online && (
        <p className="hint" role="status">
          Offline — showing last-loaded set list. Live actions are paused until you reconnect.
        </p>
      )}
      {loadError && <p className="error">{loadError}</p>}
      {actionError && <p className="error">{actionError}</p>}

      {current ? (
        <div className="now-item" aria-live="polite">
          <p className="eyebrow small">Now</p>
          <p className="now-item__title">{current.displayTitle || '(untitled)'}</p>
          <div className="now-item__controls">
            <button
              type="button"
              className="big now-item__complete"
              disabled={disabled}
              onClick={() => void runAction('complete', { type: 'complete_item', itemId: current.itemId })}
            >
              {pending === 'complete' ? 'Completing…' : 'Complete'}
            </button>
            <button
              type="button"
              className="big now-item__skip"
              disabled={disabled}
              onClick={() => void runAction('skip', { type: 'skip_item', itemId: current.itemId })}
            >
              {pending === 'skip' ? 'Skipping…' : 'Skip'}
            </button>
            <button
              type="button"
              className="big ghost"
              disabled={disabled}
              onClick={() => void runAction('clear', { type: 'clear' })}
            >
              Clear now-playing
            </button>
          </div>
        </div>
      ) : (
        <p className="muted">No current item. Start one from the list below.</p>
      )}

      {!loaded ? (
        <p aria-busy="true">Loading set list…</p>
      ) : rows.length === 0 ? (
        <p className="muted">This set list is empty.</p>
      ) : (
        <ol className="setlist-console-list">
          {rows.map(({ item, sectionName }, index) => {
            const isCurrent = item.itemId === currentItemId;
            const isPlayed = currentIndex >= 0 && index < currentIndex;
            return (
              <li
                key={item.itemId}
                className={`setlist-console-item ${isCurrent ? 'setlist-console-item--current' : ''} ${
                  isPlayed ? 'setlist-console-item--played' : ''
                }`}
              >
                <div className="setlist-console-item__label">
                  <span className="setlist-console-item__section">{sectionName}</span>
                  <span className="setlist-console-item__title">
                    {item.displayTitle || '(untitled)'}
                  </span>
                  {isCurrent && <span className="badge small">now</span>}
                </div>
                {!isCurrent &&
                  (isPlayed ? (
                    <button
                      type="button"
                      className="setlist-console-item__btn ghost"
                      disabled={disabled}
                      onClick={() => void runAction('return', { type: 'return_item', itemId: item.itemId })}
                    >
                      Return
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="setlist-console-item__btn"
                      disabled={disabled}
                      onClick={() => void runAction('start', { type: 'start_item', itemId: item.itemId })}
                    >
                      Start
                    </button>
                  ))}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
